import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import ConfirmationModal from './ConfirmationModal';
import green from '@material-ui/core/colors/green';
import { ReportService } from '../services';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Grid from '@material-ui/core/Grid';
import FormGroup from '@material-ui/core/FormGroup';
import Typography from '@material-ui/core/Typography';
import FormHelperText from '@material-ui/core/FormHelperText';
import Paper from '@material-ui/core/Paper';
import { useNotification } from '../contexts/NotificationContext';

const styles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  formSection: {
    marginBottom: theme.spacing(2),
  },
  submitButton: {
    marginLeft: '10px',
    marginTop: '25px'
  },
  confirmButton: {
    backgroundColor: green[400],
    float: 'right',
    marginTop: '16px',
    marginRight: '4px'
  },
  inputAmount: {
    float: 'right',
    marginTop: '8px',
    marginRight: '4px',
    marginBottom: '4px'
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  }
}));

const DailyProcessingReport = () => {
  const today = new Date();
  const { createNotification, createErrorNotification } = useNotification();
  const [showConfirmConfirmationBox, setShowConfirmConfirmationBox] = useState(false);
  const [fromDate, setFromDate] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7));
  const [toDate, setToDate] = useState(today);
  const [reportTypes, setReportTypes] = useState({
    refunds: false,
    payments: false,
    fundingRequests: false
  });
  const [errors, setErrors] = useState({
    dates: '',
    reportTypes: ''
  });

  const toggleConfirmConfirmation = () => setShowConfirmConfirmationBox(!showConfirmConfirmationBox);

  const handleFromDateChange = (date) => {
    setFromDate(date);
    validateDates(date, toDate);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
    validateDates(fromDate, date);
  };

  const handleReportTypeChange = (event) => {
    const { name, checked } = event.target;
    const newReportTypes = { ...reportTypes, [name]: checked };
    setReportTypes(newReportTypes);

    // Validate that at least one checkbox is selected
    const hasSelection = Object.values(newReportTypes).some(value => value);
    setErrors(prev => ({
      ...prev,
      reportTypes: hasSelection ? '' : 'Please select at least one report type'
    }));
  };

  const validateDates = (from, to) => {
    if (from && to && from > to) {
      setErrors(prev => ({
        ...prev,
        dates: 'From date must be before or equal to To date'
      }));
      return false;
    } else {
      setErrors(prev => ({
        ...prev,
        dates: ''
      }));
      return true;
    }
  };

  const validateForm = () => {
    // Validate dates
    const datesValid = validateDates(fromDate, toDate);

    // Validate at least one checkbox is selected
    const hasSelection = Object.values(reportTypes).some(value => value);
    if (!hasSelection) {
      setErrors(prev => ({
        ...prev,
        reportTypes: 'Please select at least one report type'
      }));
      return false;
    }

    return datesValid && hasSelection;
  };

  const handleConfirmGenerateReport = async () => {
    if (validateForm()) {
      toggleConfirmConfirmation();
    }
  };

  const handleConfirmPullPaymentsButton = async () => {
    const selectedReportTypes = Object.entries(reportTypes)
      .filter(([_, selected]) => selected)
      .map(([type]) => type);

    try {
      const response = await ReportService.dailyProcessingReport(fromDate, toDate, selectedReportTypes);
      if (response.status === 200) {
        createNotification('Report requested successfully, check your email in a few minutes to download the file(s)');
      } else {
        createErrorNotification('Failed to generate report - please try again later');
      }
    } catch (error) {
      createErrorNotification('Error generating report: ' + (error.message || 'Unknown error'));
    }

    toggleConfirmConfirmation();
  };

  const classes = styles();

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" gutterBottom>
        Generate Daily Processing Report
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} className={classes.formSection}>
          <Typography variant="subtitle1" gutterBottom>
            Date Range
          </Typography>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={6}>
                <KeyboardDatePicker
                  fullWidth
                  margin="normal"
                  id="from-date-picker"
                  label="From Date"
                  format="MM/dd/yyyy"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  KeyboardButtonProps={{
                    'aria-label': 'change from date',
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={6}>
                <KeyboardDatePicker
                  fullWidth
                  margin="normal"
                  id="to-date-picker"
                  label="To Date"
                  format="MM/dd/yyyy"
                  value={toDate}
                  onChange={handleToDateChange}
                  KeyboardButtonProps={{
                    'aria-label': 'change to date',
                  }}
                  maxDate={today}
                />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
          {errors.dates && (
            <Typography className={classes.errorText}>
              {errors.dates}
            </Typography>
          )}
        </Grid>

        <Grid item xs={6} className={classes.formSection}>
          <Typography variant="subtitle1" gutterBottom>
            Report Types
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reportTypes.refunds}
                  onChange={handleReportTypeChange}
                  name="refunds"
                  color="primary"
                />
              }
              label="Refunds"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={reportTypes.payments}
                  onChange={handleReportTypeChange}
                  name="payments"
                  color="primary"
                />
              }
              label="Payments"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={reportTypes.fundingRequests}
                  onChange={handleReportTypeChange}
                  name="fundingRequests"
                  color="primary"
                />
              }
              label="Funding Requests"
            />
          </FormGroup>
          {errors.reportTypes && (
            <FormHelperText error>
              {errors.reportTypes}
            </FormHelperText>
          )}
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            className={classes.confirmButton}
            onClick={handleConfirmGenerateReport}
            disabled={!!errors.dates || !!errors.reportTypes}
          >
            Generate Report
          </Button>
        </Grid>
      </Grid>

      <ConfirmationModal
        title="Confirm"
        message="Are you sure you want to generate a CSV report of selected records?"
        onConfirm={handleConfirmPullPaymentsButton}
        onCancel={toggleConfirmConfirmation}
        showConfirmationBox={showConfirmConfirmationBox}
      />
    </Paper>
  );
};

export default DailyProcessingReport;
